import { useState, useEffect } from 'react';
import './UserManagement.css';
import UserForm from './UserForm';

/**
 * User Management Component
 * Manage staff users - create, edit, delete, assign roles
 */

function UserManagement({ apiBase }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, [apiBase]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/admin/users`, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch users');

      const data = await response.json();
      if (data.ok && Array.isArray(data.data)) {
        setUsers(data.data);
      }
      setError('');
    } catch (err) {
      setError(err.message);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`${apiBase}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const responseData = await response.json();

      if (response.ok && responseData.ok) {
        setUsers(users.filter(u => u.id !== userId));
        setError('');
      } else {
        const errorMsg = responseData.message || 'Failed to delete user';
        setError(errorMsg);
        console.error('Delete user error:', {
          status: response.status,
          response: responseData,
          userId: userId,
          apiBase: apiBase
        });
      }
    } catch (err) {
      const errorMsg = err.message || 'Delete failed';
      setError(errorMsg);
      console.error('Delete user exception:', err, {
        userId: userId,
        apiBase: apiBase
      });
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const handleFormSuccess = () => {
    fetchUsers();
    handleFormClose();
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || user.role_codes.includes(filterRole);
    const matchesStatus = filterStatus === 'all' || (
      (filterStatus === 'active' && user.is_active) ||
      (filterStatus === 'inactive' && !user.is_active)
    );

    return matchesSearch && matchesRole && matchesStatus;
  });

  const EditIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
  );

  const DeleteIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      <line x1="10" y1="11" x2="10" y2="17"></line>
      <line x1="14" y1="11" x2="14" y2="17"></line>
    </svg>
  );

  return (
    <div className="user-management">
      <div className="page-header">
        <div>
          <h2>User Management</h2>
          <p>Manage your team members and assign roles</p>
        </div>
        <button className="btn-primary" onClick={handleAddUser}>
          Add New User
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <div>
            <strong>Error</strong>
            <p>{error}</p>
          </div>
          <button onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      <div className="content-card">
        <div className="card-header">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          <div className="filters">
            <select 
              value={filterRole} 
              onChange={(e) => setFilterRole(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin (Website/Content)</option>
              <option value="admissions">Admin Admissions</option>
              <option value="approver">Principal (Content Approver)</option>
            </select>

            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {showForm && (
          <UserForm
            user={editingUser}
            apiBase={apiBase}
            onClose={handleFormClose}
            onSuccess={handleFormSuccess}
          />
        )}

        {loading ? (
          <div className="empty-state">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <h3>No Users Found</h3>
            <p>Get started by creating your first user</p>
            <button className="btn-outline" onClick={handleAddUser}>
              Create User
            </button>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">{user.full_name.charAt(0).toUpperCase()}</div>
                        <span>{user.full_name}</span>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <div className="roles-badges">
                        {user.role_codes.map(role => (
                          <span key={role} className={`badge badge-${role}`}>
                            {role === 'super_admin' ? 'Super Admin' : role.charAt(0).toUpperCase() + role.slice(1)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn-action btn-edit"
                          onClick={() => handleEditUser(user)}
                          title="Edit user"
                        >
                          <EditIcon />
                        </button>
                        <button 
                          className="btn-action btn-delete"
                          onClick={() => handleDeleteUser(user.id)}
                          title="Delete user"
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagement;
